import { Event } from 'services/events/types';
import { formatShortDueDate } from 'utils/date';

const IGNORED_PROJECT_DIFF_KEYS = new Set([
  '__v',
  '_id',
  'archivedAt',
  'cancelledAt',
  'completedAt',
  'createdAt',
  'id',
  'isCancelled',
  'isCompleted',
  'modifiedAt',
  'userId',
]);

const PROJECT_FIELD_LABEL_KEYS: Record<string, string> = {
  assignee: 'assignee',
  cancelReason: 'cancelReason',
  color: 'color',
  description: 'description',
  dueAt: 'deadline',
  isArchived: 'archive',
  members: 'members',
  name: 'name',
  parentProject: 'parentProject',
  startAt: 'startDate',
  status: 'status',
};

const PROJECT_HISTORY_FIELD_ORDER = [
  'name',
  'description',
  'status',
  'assignee',
  'cancelReason',
  'color',
  'startAt',
  'dueAt',
  'parentProject',
  'members',
  'isArchived',
];

type Translate = (key: string, options?: Record<string, unknown>) => string;

type DatedField = 'dueAt' | 'startAt';

interface SummarizeProjectEventOptions {
  projectName?: (projectId: string) => string | undefined;
  userName?: (userId: string) => string | undefined;
}

const collectKeys = (obj: Record<string, unknown> | undefined, keys: Set<string>) => {
  if (!obj) {
    return;
  }
  Object.keys(obj).forEach(key => {
    if (!IGNORED_PROJECT_DIFF_KEYS.has(key)) {
      keys.add(key);
    }
  });
};

const getChangedProjectFieldKeys = (event: Event): string[] => {
  const keys = new Set<string>();
  collectKeys(event.diff, keys);
  collectKeys(event.detailedDiff?.added, keys);
  collectKeys(event.detailedDiff?.deleted, keys);
  collectKeys(event.detailedDiff?.updated, keys);

  return [...keys].sort((a, b) => {
    const aIndex = PROJECT_HISTORY_FIELD_ORDER.indexOf(a);
    const bIndex = PROJECT_HISTORY_FIELD_ORDER.indexOf(b);
    const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.localeCompare(b);
  });
};

const joinWithAnd = (items: string[], andWord: string): string => {
  if (items.length === 0) {
    return '';
  }
  if (items.length === 1) {
    return items[0];
  }
  if (items.length === 2) {
    return `${items[0]} ${andWord} ${items[1]}`;
  }
  return `${items.slice(0, -1).join(', ')} ${andWord} ${items[items.length - 1]}`;
};

const readSnapshotField = (event: Event, field: DatedField | 'assignee' | 'parentProject'): unknown => {
  const current = event.project;
  const previous = event.oldProject;
  if (current && field in current) {
    return current[field];
  }
  if (event.diff && field in event.diff) {
    return event.diff[field];
  }
  if (event.detailedDiff?.updated && field in event.detailedDiff.updated) {
    return event.detailedDiff.updated[field];
  }
  if (event.detailedDiff?.added && field in event.detailedDiff.added) {
    return event.detailedDiff.added[field];
  }
  if (current && previous && field in previous && !(field in current)) {
    return null;
  }
  if (event.detailedDiff?.deleted && field in event.detailedDiff.deleted) {
    return null;
  }
  return undefined;
};

const describeDate = (
  event: Event,
  field: DatedField,
  t: Translate,
  setKey: string,
  clearedKey: string,
): string | null => {
  const value = readSnapshotField(event, field);
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t(clearedKey);
  }
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return t(setKey, {
    date: formatShortDueDate(date),
  });
};

const describeAssignee = (
  event: Event,
  t: Translate,
  userName?: (userId: string) => string | undefined,
): string | null => {
  const value = readSnapshotField(event, 'assignee');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('projectHistoryAssigneeCleared');
  }
  if (typeof value !== 'string') {
    return null;
  }
  return t('projectHistoryAssigneeSet', {
    assignee: userName?.(value) || t('unknownUser'),
  });
};

const describeParentProject = (
  event: Event,
  t: Translate,
  projectName?: (projectId: string) => string | undefined,
): string | null => {
  const value = readSnapshotField(event, 'parentProject');
  if (value === undefined) {
    return null;
  }
  if (value === null || value === '') {
    return t('projectHistoryParentCleared');
  }
  const id = String(value);
  return t('projectHistoryParentSet', {
    project: projectName?.(id) || id,
  });
};

const summarizeProjectEvent = (
  event: Event,
  t: Translate,
  options?: SummarizeProjectEventOptions,
): string => {
  if (event.eventType === 'CREATED') {
    return t('projectCreated');
  }
  if (event.eventType === 'DELETED') {
    return t('projectDeleted');
  }

  const fieldKeys = getChangedProjectFieldKeys(event);
  if (fieldKeys.length === 0) {
    return t('projectUpdated');
  }

  const labels: string[] = [];
  const details: string[] = [];
  fieldKeys.forEach(key => {
    if (key === 'assignee') {
      const assignee = describeAssignee(event, t, options?.userName);
      if (assignee) {
        details.push(assignee);
        return;
      }
    }
    if (key === 'dueAt') {
      const deadline = describeDate(
        event,
        'dueAt',
        t,
        'projectHistoryDeadlineSet',
        'projectHistoryDeadlineCleared',
      );
      if (deadline) {
        details.push(deadline);
        return;
      }
    }
    if (key === 'startAt') {
      const startDate = describeDate(
        event,
        'startAt',
        t,
        'projectHistoryStartDateSet',
        'projectHistoryStartDateCleared',
      );
      if (startDate) {
        details.push(startDate);
        return;
      }
    }
    if (key === 'parentProject') {
      const parent = describeParentProject(event, t, options?.projectName);
      if (parent) {
        details.push(parent);
        return;
      }
    }
    labels.push(t(PROJECT_FIELD_LABEL_KEYS[key] ?? key));
  });

  const parts = [
    ...(labels.length > 0
      ? [t('projectUpdatedFields', { fields: joinWithAnd(labels, t('and')) })]
      : []),
    ...details,
  ];
  if (parts.length === 0) {
    return t('projectUpdated');
  }
  return parts.join('. ');
};

export {
  getChangedProjectFieldKeys,
  summarizeProjectEvent,
};
